import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  Avatar,
  styled,
  Tooltip,
  List,
  ToggleButton,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { IconButton } from "@mui/material";
import {
  Menu as MenuIcon,
  MenuOpen as MenuOpenIcon,
} from "@mui/icons-material";

import {
  Person as PersonIcon,
  Work as WorkIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  Event as EventIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation, Routes, Route } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useProfile } from "../../../contexts/ProfileContext";
import UserSettings from "../UserSettings";
import Cookies from "js-cookie";
import ScrollToTop from "./ScrollToTop";

const drawerWidth = 240;

const Sidebar = styled(Box)(({ theme }) => ({
  width: drawerWidth,
  height: "100vh",
  position: "fixed",
  top: 64, // below AppBar
  left: 0,
  background: "#fff",
  borderRight: `1px solid ${theme.palette.divider}`,
  display: "flex",
  flexDirection: "column",
  paddingTop: theme.spacing(4),
  zIndex: theme.zIndex.appBar - 1,
}));

// const Sidebar = styled(Box)(({ theme }) => ({
//   width: drawerWidth,
//   height: "100vh",
//   position: "fixed",
//   top: 64, // below AppBar
//   left: 0,
//   background: "#352C59", // Deep Violet for elevated surface
//   borderRight: `1px solid ${theme.palette.divider}`,
//   display: "flex",
//   flexDirection: "column",
//   paddingTop: theme.spacing(4),
//   zIndex: theme.zIndex.appBar - 1,
//   boxShadow: "4px 0 16px rgba(0, 0, 0, 0.3)",
// }));
const Main = styled("main")(({ theme }) => ({
  flexGrow: 1,
  paddingTop: theme.spacing(3),
  marginLeft: drawerWidth,
  marginTop: 64,
  width: `calc(100% - ${drawerWidth}px)`,
}));

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3500";

const CandidateLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isProfileReadOnly, profileReviewStatus, resetProfileContext } =
    useProfile();

  const [userInitials, setUserInitials] = useState("");
  const [userType, setUserType] = useState("candidate");
  const [isOpenSidebar, setIsOpenSidebar] = useState(
    Cookies.get("sidebarState")
      ? Cookies.get("sidebarState") !== "closed"
      : false,
  );

  // persist state to localStorage whenever it changes
  useEffect(() => {
    Cookies.set("sidebarState", isOpenSidebar ? "open" : "closed", {
      expires: 7,
    });
  }, [isOpenSidebar]);

  useEffect(() => {
    const token = Cookies.get("token");
    if (token) {
      const decodedToken = jwtDecode(token);
      setUserType(decodedToken.userType);
      const email = decodedToken.email || "";
      const initials = email.split("@")[0].substring(0, 2).toUpperCase();
      setUserInitials(initials);
    }
  }, []);

  useEffect(() => {
    // Only for candidate
    if (userType !== "candidate" || !profileReviewStatus) {
      return;
    }
    // If Under Review, redirect to Profile Review step
    if (profileReviewStatus.review_status === "Under Review") {
      if (location.pathname !== "/candidate/profile/review") {
        navigate("/candidate/profile/review", { replace: true });
      }
      return;
    }
    // if (
    //   profileReviewStatus.hasPendingVersion &&
    //   profileReviewStatus.review_status === "Approved"
    // ) {
    //   if (location.pathname !== "/candidate/profile/review") {
    //     navigate("/candidate/profile/review", { replace: true });
    //   }
    //   return;
    // }
    // For Approved+Active and all other cases, allow navigation to any page (no redirect)
    // (Removed redirect from profile to search-jobs for approved+active users)
  }, [profileReviewStatus, userType, location.pathname, navigate]);

  // Menu logic
  const menuItems = [
    { text: "Profile", icon: <PersonIcon />, path: "/candidate/profile" },
    {
      text: "Interview Dashboard",
      icon: <EventIcon />,
      path: "/candidate/interview-dashboard",
    },
    // { text: "Search Jobs", icon: <WorkIcon />, path: "/candidate/search-jobs" }, // Commented out - functionality preserved
    // { text: 'Schedule Job Interview', icon: <EventIcon />, path: '/candidate/schedule-interview' }, // Commented out - functionality preserved
    {
      text: "User Settings",
      icon: <SettingsIcon />,
      path: "/candidate/settings",
    },
  ];

  // Disable logic for candidate
  const isUnderReview =
    userType === "candidate" &&
    profileReviewStatus &&
    profileReviewStatus.review_status === "Under Review";
  // const isUpdateReview =
  //   userType === "candidate" &&
  //   profileReviewStatus &&
  //   profileReviewStatus.review_status === "Approved" &&
  //   !profileReviewStatus.hasPendingVersion
  //     ? true
  //     : false;
  const isApprovedActive =
    userType === "candidate" &&
    profileReviewStatus &&
    profileReviewStatus.review_status === "Approved" &&
    (profileReviewStatus.isActive === 1 ||
      profileReviewStatus.isActive === true);
  const isApproved =
    userType === "candidate" &&
    profileReviewStatus &&
    profileReviewStatus.review_status === "Approved";

  // Only enable Search Jobs if Approved+Active
  const shouldDisableSearchJobs = !isApprovedActive;
  // Only enable Schedule Job Interview if Approved (regardless of active status)
  const shouldDisableScheduleInterview = !isApproved;

  // NEW: If Approved+Active, do not disable or hide any menu
  const allMenusEnabled = isApprovedActive;

  const handleLogout = () => {
    // Reset profile context state
    if (resetProfileContext) {
      resetProfileContext();
    }

    Cookies.remove("token");
    Cookies.remove("sidebarState");
    localStorage.clear();
    navigate(`/login?userType=${userType}`);
  };

  const handleMenuClick = (path, disabled) => {
    if (!disabled) {
      navigate(path);
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <ScrollToTop />
      <AppBar
        position="fixed"
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, boxShadow: "none" }}
      >
        <Toolbar
          sx={{ minHeight: 64, display: "flex", alignItems: "center", px: 3 }}
        >
          <Tooltip title={isOpenSidebar ? "close sidebar" : "open sidebar"}>
            <IconButton
              color="inherit"
              aria-label="toggle sidebar"
              onClick={() => setIsOpenSidebar(!isOpenSidebar)}
              disabled={false} // set true if needed
              sx={{
                mr: 2,
                p: 1.5,
                borderRadius: 2,
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              {isOpenSidebar ? <MenuOpenIcon /> : <MenuIcon />}
            </IconButton>
          </Tooltip>
          <Box sx={{ display: "flex", alignItems: "center", height: 48 }}>
            <Box
              component="img"
              src="/bb-logo.png"
              alt="BB Logo"
              sx={{ height: 40, width: 40, mr: 2, display: "block" }}
            />
            <Typography
              variant="h6"
              component="div"
              sx={{ fontWeight: 600, fontSize: 24, letterSpacing: 0.5 }}
            >
              EQuAlly
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Tooltip title="Logout">
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                sx={{
                  borderRadius: 20,
                  borderColor: "rgba(255, 255, 255, 0.5)",
                  height: 40,
                  px: 2.5,
                  fontWeight: 500,
                  fontSize: 16,
                  "&:hover": {
                    borderColor: "white",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                Logout
              </Button>
            </Tooltip>
            <Tooltip title="Your Profile">
              <Avatar
                sx={{
                  bgcolor: "secondary.main",
                  cursor: "pointer",
                  width: 40,
                  height: 40,
                  fontWeight: 600,
                  fontSize: 18,
                  "&:hover": {
                    bgcolor: "secondary.dark",
                  },
                }}
                onClick={() => navigate("/candidate/profile")}
              >
                {userInitials}
              </Avatar>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      <Sidebar
        sx={{
          width: isOpenSidebar ? drawerWidth : 64, // full vs collapsed width
          transition: "width 0.3s",
          overflowX: "hidden",
          paddingTop: 2,
        }}
      >
        <List sx={{ padding: 0 }}>
          {menuItems.map((item) => {
            if (isUnderReview && item.text === "Search Jobs") return null;

            const isSearchJobs = item.text === "Search Jobs";
            const isScheduleInterview = item.text === "Schedule Interview";

            const disabled = allMenusEnabled
              ? false
              : (isSearchJobs && shouldDisableSearchJobs) ||
                (isScheduleInterview && shouldDisableScheduleInterview);

            let tooltipMessage = "";
            if (!allMenusEnabled) {
              if (isSearchJobs && shouldDisableSearchJobs) {
                tooltipMessage =
                  "Profile must be approved and active to access this section";
              } else if (
                isScheduleInterview &&
                shouldDisableScheduleInterview
              ) {
                tooltipMessage =
                  "Profile must be approved to schedule interviews";
              }
            }

            return (
              <Tooltip
                key={item.text}
                title={!isOpenSidebar ? item.text : tooltipMessage}
                placement="right"
                arrow
                disableHoverListener={!disabled && isOpenSidebar}
              >
                <span style={{ display: "block" }}>
                  <ListItem
                    button
                    onClick={() => handleMenuClick(item.path, disabled)}
                    selected={location.pathname === item.path}
                    disabled={disabled}
                    sx={{
                      minHeight: 48,
                      justifyContent: isOpenSidebar ? "flex-start" : "center", // Changed from "initial"
                      px: isOpenSidebar ? 2 : 1, // Reduced padding when closed
                      py: 1.5,
                      width: "100%", // Ensure full width usage
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color:
                          location.pathname === item.path
                            ? "primary.main"
                            : "inherit",
                        minWidth: isOpenSidebar ? 40 : 24, // Adjust icon spacing
                        mr: isOpenSidebar ? 1.5 : 0, // Reduced margin
                        justifyContent: "center",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {isOpenSidebar && (
                      <ListItemText
                        primary={item.text}
                        sx={{
                          margin: 0, // Remove default margins
                          "& .MuiTypography-root": {
                            fontSize: "0.875rem", // Slightly smaller font
                            fontWeight:
                              location.pathname === item.path ? 600 : 400,
                            color:
                              location.pathname === item.path
                                ? "primary.main"
                                : "inherit",
                            whiteSpace: "nowrap",
                            overflow: "visible", // Changed from hidden to visible
                            textOverflow: "clip", // Changed from ellipsis
                            lineHeight: 1.2,
                          },
                        }}
                      />
                    )}
                  </ListItem>
                </span>
              </Tooltip>
            );
          })}
        </List>
      </Sidebar>

      <Main
        sx={{
          flexGrow: 1,
          transition: (theme) =>
            theme.transitions.create(["margin", "width"], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          marginLeft: isOpenSidebar ? `${drawerWidth}px` : "64px",
          width: isOpenSidebar ? `calc(100% - ${drawerWidth}px)` : "100%",
        }}
      >
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            width: "100%",
            py: 3,
            px: 0,
          }}
        >
          <Routes>
            <Route path="/candidate/settings" element={<UserSettings />} />
            <Route path="*" element={children} />
          </Routes>
        </Container>
      </Main>

      {/* <Main>
        <FormControlLabel
      control={
        <IOSSwitch
          sx={{ m: 1 }}
          checked={isOpenSidebar}
          onChange={(e) => setIsOpenSidebar(e.target.checked)}
        />
      }
      label={isOpenSidebar ? 'Sidebar Open' : 'Sidebar Closed'}
    />
        <Container 
          maxWidth={false} 
          disableGutters
          sx={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            width: '100%',
            py: 3,
            px: 0
          }}
        >
          <Routes>
            <Route path="/candidate/settings" element={<UserSettings />} />
            <Route path="*" element={children} />
          </Routes>
        </Container>
      </Main> */}
    </Box>
  );
};

export default CandidateLayout;
